import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, ExternalLink, RotateCcw, ShieldOff, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { DashboardLayout, Panel, StatCard, StatusBadge } from "@/components/DashboardLayout";
import { Avatar } from "@/components/Avatar";
import { PasswordInput } from "@/components/PasswordInput";
import { api, apiError, BACKEND_URL, naira } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = ["queue", "disputes", "licences", "providers", "users", "moderation", "team", "security", "audit"];
const STATUS_FILTERS = ["SUBMITTED", "UNDER_REVIEW", "CORRECTION_REQUESTED", "VERIFIED", "REJECTED"];
const USER_STATES = [["active", "Active"], ["blocked", "Blocked"], ["removed", "Removed"], ["all", "All"]];

export default function AdminDashboard() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("queue");
  const [status, setStatus] = useState("SUBMITTED");
  const [overview, setOverview] = useState(null);
  const [queue, setQueue] = useState([]);
  const [providers, setProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [disputeNotes, setDisputeNotes] = useState({});
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: "", full_name: "", role: "ADMIN" });
  const [inviteLink, setInviteLink] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [userState, setUserState] = useState("active");
  const [moderation, setModeration] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [adminForm, setAdminForm] = useState({ full_name: "", email: "", phone: "", role: "ADMIN", temporary_password: "" });

  const loadAdmins = () =>
    api
      .get("/api/admin/users", { params: { state: "all" } })
      .then((r) => setAdmins(r.data.filter((u) => ["ADMIN", "SUPER_ADMIN"].includes(u.role) && u.moderation_state !== "REMOVED")))
      .catch(() => {});

  const createAdmin = async () => {
    try {
      const { data } = await api.post("/api/admin/team", adminForm);
      toast.success(`${adminForm.full_name} can now sign in at /admin/login`, { description: data.note });
      setAdminForm({ full_name: "", email: "", phone: "", role: "ADMIN", temporary_password: "" });
      loadAdmins();
      loadLogs();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.post(`/api/admin/users/${id}/role`, { role });
      toast.success(role === "SUPER_ADMIN" ? "Promoted to super administrator" : "Set to administrator");
      loadAdmins();
      loadLogs();
    } catch (e) {
      toast.error(apiError(e));
      loadAdmins();
    }
  };

  const refreshUsers = () => {
    loadUsers();
    loadModeration();
    loadOverview();
  };

  const restoreUser = async (id) => {
    try {
      await api.post(`/api/admin/users/${id}/restore`);
      toast.success("Account restored");
      refreshUsers();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const blockUser = async () => {
    try {
      await api.post(`/api/admin/users/${blockTarget.id}/block`, { reason: blockReason });
      toast.success(`${blockTarget.full_name} has been blocked`);
      setBlockTarget(null);
      setBlockReason("");
      refreshUsers();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const unblockUser = async (id) => {
    try {
      await api.post(`/api/admin/users/${id}/unblock`);
      toast.success("Account restored");
      refreshUsers();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/api/admin/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.full_name} has been removed`);
      setDeleteTarget(null);
      refreshUsers();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const [expiryEdits, setExpiryEdits] = useState({});
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");

  const loadOverview = () => api.get("/api/admin/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(apiError(e)));
  const loadQueue = (s = status) => api.get("/api/admin/kyc", { params: s ? { status: s } : {} }).then((r) => setQueue(r.data)).catch(() => {});
  const loadProviders = () => api.get("/api/admin/providers").then((r) => setProviders(r.data)).catch(() => {});
  const loadUsers = (s = userState) => api.get("/api/admin/users", { params: { state: s } }).then((r) => setUsers(r.data)).catch(() => {});
  const loadModeration = () =>
    Promise.all([
      api.get("/api/admin/users", { params: { state: "blocked" } }),
      api.get("/api/admin/users", { params: { state: "removed" } }),
    ])
      .then(([b, d]) => setModeration([...b.data, ...d.data]))
      .catch(() => {});
  const loadLogs = () => api.get("/api/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {});
  const loadAlerts = () => api.get("/api/admin/licence-alerts").then((r) => setAlerts(r.data.items)).catch(() => {});
  const loadDisputes = () => api.get("/api/admin/disputes").then((r) => setDisputes(r.data.items)).catch(() => {});
  const loadInvites = () => api.get("/api/admin/invites").then((r) => setInvites(r.data.items)).catch(() => {});

  const sendInvite = async () => {
    try {
      const { data } = await api.post("/api/admin/invites", inviteForm);
      setInviteLink(`${window.location.origin}${data.invite_link}`);
      toast.success("Invitation created", { description: "Email delivery is mocked — share the link below." });
      setInviteForm({ email: "", full_name: "", role: "ADMIN" });
      loadInvites();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const revokeInvite = async (id) => {
    try {
      await api.post(`/api/admin/invites/${id}/revoke`);
      toast.success("Invitation revoked");
      loadInvites();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changePassword = async () => {
    try {
      const { data } = await api.post("/api/auth/change-password", pw);
      if (data.access_token) localStorage.setItem("cn_token", data.access_token);
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resolveDispute = async (bookingId, action) => {
    try {
      await api.post(`/api/admin/disputes/${bookingId}/resolve`, { action, notes: disputeNotes[bookingId] || null });
      toast.success(action === "RELEASE" ? "Funds released to the provider" : "Funds refunded to the client");
      loadDisputes();
      loadOverview();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    loadOverview();
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "providers") loadProviders();
    if (tab === "licences") loadAlerts();
    if (tab === "disputes") loadDisputes();
    if (tab === "team") {
      loadInvites();
      loadAdmins();
    }
    if (tab === "users") loadUsers();
    if (tab === "moderation") loadModeration();
    if (tab === "audit") loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const open = async (id) => {
    try {
      const { data } = await api.get(`/api/admin/kyc/${id}`);
      setDetail(data);
      setNotes("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const act = async (action) => {
    try {
      await api.post(`/api/admin/kyc/${detail.id}/${action}`, { notes });
      toast.success(`KYC ${action.replaceAll("-", " ")} recorded`);
      setDetail(null);
      loadQueue();
      loadOverview();
      loadLogs();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const updateLicence = async (providerId) => {
    const value = expiryEdits[providerId];
    if (!value) {
      toast.error("Choose a new expiry date first");
      return;
    }
    try {
      await api.put(`/api/admin/providers/${providerId}/licence`, { license_expiry_date: value });
      toast.success("Licence expiry updated");
      loadAlerts();
      loadOverview();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const providerAction = async (id, action) => {
    try {
      await api.post(`/api/admin/providers/${id}/${action}`, { notes: "Admin action" });
      toast.success(`Provider ${action}d`);
      loadProviders();
      loadOverview();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <DashboardLayout title="CARENEST administration" subtitle="KYC review, provider oversight, users and the audit trail.">
      <div className="grid gap-6">
        {overview && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard label="Total users" value={overview.total_users} testId="admin-stat-users" />
            <StatCard label="KYC awaiting review" value={overview.kyc_pending} testId="admin-stat-pending" />
            <StatCard label="Verified providers" value={overview.verified_providers} testId="admin-stat-providers" />
            <StatCard label="Licence alerts" value={overview.licence_expiring_soon + overview.licence_expired} hint={`${overview.licence_expired} expired`} testId="admin-stat-licences" />
            <StatCard label="Open bookings" value={overview.bookings_open} hint={`${overview.bookings_total} total`} testId="admin-stat-bookings" />
            <StatCard label="Open disputes" value={overview.open_disputes} hint={`${overview.pending_release} awaiting release`} testId="admin-stat-disputes" />
            <StatCard label="Audit events" value={overview.audit_events} testId="admin-stat-audit" />
          </div>
        )}

        {user?.must_change_password && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="must-change-password-banner">
            <strong>Change your temporary password.</strong> Whoever created this account knows the password you just
            used.{" "}
            <button onClick={() => setTab("security")} className="font-semibold underline" data-testid="must-change-password-link">
              Set a new password now
            </button>
          </div>
        )}


        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${
                tab === t ? "bg-emerald-900 text-white" : "bg-white text-slate-600 hover:bg-emerald-50"
              }`}
            >
              {t === "queue" ? "KYC queue" : t}
            </button>
          ))}
        </div>

        {tab === "queue" && (
          <Panel title="KYC review queue" testId="admin-kyc-panel">
            <div className="mb-5 flex flex-wrap gap-2">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatus(s); loadQueue(s); }}
                  data-testid={`admin-filter-${s.toLowerCase()}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                    status === s ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500"
                  }`}
                >
                  {s.replaceAll("_", " ")}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-kyc-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
                    <th className="py-3 pr-4">Applicant</th>
                    <th className="py-3 pr-4">Role</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Docs</th>
                    <th className="py-3" />
                  </tr>
                </thead>
                <tbody>
                  {queue.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`admin-kyc-row-${r.id}`}>
                      <td className="py-3 pr-4">
                        <p className="font-medium text-slate-900">{r.full_name}</p>
                        <p className="text-xs text-slate-500">{r.email}</p>
                      </td>
                      <td className="py-3 pr-4 text-xs text-slate-600">
                        {r.role.replaceAll("_", " ")}
                        {r.primary_specialty && (
                          <p className="mt-0.5 text-[11px] font-medium text-emerald-800" data-testid={`admin-kyc-specialty-${r.id}`}>
                            {r.primary_specialty}
                          </p>
                        )}
                      </td>
                      <td className="py-3 pr-4"><StatusBadge status={r.status} /></td>
                      <td className="py-3 pr-4 text-xs text-slate-500">{r.document_count}</td>
                      <td className="py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => open(r.id)} data-testid={`admin-review-${r.id}`}>Review</Button>
                      </td>
                    </tr>
                  ))}
                  {queue.length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-sm text-slate-500" data-testid="admin-kyc-empty">No records with this status.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {tab === "disputes" && (
          <Panel title="Escrow disputes" description="Funds stay held until you decide. Releasing pays the provider; refunding returns the money to the family." testId="admin-disputes-panel">
            {disputes.length ? (
              <ul className="divide-y divide-slate-100" data-testid="admin-disputes-list">
                {disputes.map((d) => (
                  <li key={d.booking_id} className="py-5" data-testid={`admin-dispute-row-${d.booking_id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-display font-bold text-slate-900">{d.service_name}</p>
                        <p className="text-sm text-slate-600">
                          {d.client_name} vs {d.provider_name} · {naira(d.fee_ngn)}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          For {d.beneficiary_name} · visit {new Date(d.scheduled_at).toLocaleString()}
                        </p>
                        <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                          “{d.dispute?.reason}”
                        </p>
                        {d.dispute?.evidence_urls?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-dispute-evidence-${d.booking_id}`}>
                            {d.dispute.evidence_urls.map((e, i) => (
                              <a
                                key={e.file_id}
                                href={`${BACKEND_URL}${e.url}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`admin-dispute-evidence-link-${e.file_id}`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-400"
                              >
                                <ExternalLink className="h-3.5 w-3.5" /> Evidence {i + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
                        {d.payment_status}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        placeholder="Resolution notes"
                        className="h-9 min-w-[220px] flex-1 rounded-md border border-input bg-background px-3 text-sm"
                        value={disputeNotes[d.booking_id] || ""}
                        onChange={(e) => setDisputeNotes({ ...disputeNotes, [d.booking_id]: e.target.value })}
                        data-testid={`admin-dispute-notes-${d.booking_id}`}
                      />
                      <Button size="sm" className="rounded-full bg-emerald-900" onClick={() => resolveDispute(d.booking_id, "RELEASE")} data-testid={`admin-dispute-release-${d.booking_id}`}>
                        Release to provider
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full border-orange-300 text-orange-700" onClick={() => resolveDispute(d.booking_id, "REFUND")} data-testid={`admin-dispute-refund-${d.booking_id}`}>
                        Refund client
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500" data-testid="admin-disputes-empty">No open disputes.</p>
            )}
          </Panel>
        )}

        {tab === "licences" && (
          <Panel title="Licence expiry alerts" description="Verified providers whose professional licence has expired or expires within 60 days. Expired licences are hidden from the marketplace automatically." testId="admin-licences-panel">
            {alerts.length ? (
              <ul className="divide-y divide-slate-100" data-testid="admin-licence-list">
                {alerts.map((a) => (
                  <li key={a.provider_id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`admin-licence-row-${a.provider_id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{a.full_name}</p>
                      <p className="text-xs text-slate-500">
                        {a.professional_category} · {a.registration_body} · {a.license_serial_number} · expires {a.license_expiry_date}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                          a.licence_state === "EXPIRED"
                            ? "border-red-200 bg-red-50 text-red-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                        }`}
                        data-testid={`admin-licence-state-${a.provider_id}`}
                      >
                        {a.licence_state.replaceAll("_", " ")}
                      </span>
                      <input
                        type="date"
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={expiryEdits[a.provider_id] ?? a.license_expiry_date ?? ""}
                        onChange={(e) => setExpiryEdits({ ...expiryEdits, [a.provider_id]: e.target.value })}
                        data-testid={`admin-licence-date-${a.provider_id}`}
                      />
                      <Button size="sm" variant="outline" onClick={() => updateLicence(a.provider_id)} data-testid={`admin-licence-save-${a.provider_id}`}>
                        Update
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500" data-testid="admin-licence-empty">No licences expiring within 60 days.</p>
            )}
          </Panel>
        )}

        {tab === "providers" && (
          <Panel title="Service providers" testId="admin-providers-panel">
            <ul className="divide-y divide-slate-100">
              {providers.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`admin-provider-row-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">{p.full_name}</p>
                    <p className="text-xs text-slate-500">
                      {p.professional_category}
                      {p.primary_specialty ? ` · ${p.primary_specialty}` : ""} · {p.registration_body} {p.registration_number}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={p.verification_status} />
                    {p.verification_status === "VERIFIED" ? (
                      <Button size="sm" variant="outline" onClick={() => providerAction(p.id, "suspend")} data-testid={`admin-suspend-${p.id}`}>
                        <ShieldOff className="mr-1.5 h-3.5 w-3.5" /> Suspend
                      </Button>
                    ) : p.verification_status === "SUSPENDED" ? (
                      <Button size="sm" variant="outline" onClick={() => providerAction(p.id, "reinstate")} data-testid={`admin-reinstate-${p.id}`}>
                        <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Reinstate
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {tab === "users" && (
          <Panel title="Users" testId="admin-users-panel">
            <div className="mb-4 flex flex-wrap gap-2" data-testid="user-state-filters">
              {USER_STATES.map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => { setUserState(value); loadUsers(value); }}
                  className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                    userState === value ? "border-emerald-700 bg-emerald-900 text-white" : "border-slate-200 text-slate-600 hover:border-emerald-300"
                  }`}
                  data-testid={`user-state-${value}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
                    <th className="py-3 pr-4">Name</th><th className="py-3 pr-4">Email</th><th className="py-3 pr-4">Role</th><th className="py-3 pr-4">KYC</th><th className="py-3" />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100" data-testid={`admin-user-row-${u.id}`}>
                      <td className="py-3 pr-4 font-medium text-slate-900">
                        <span className="flex items-center gap-2.5">
                          <Avatar name={u.full_name} src={u.avatar_url} size="sm" testId={`admin-user-avatar-${u.id}`} />
                          <span>{u.full_name}</span>
                        </span>
                        {u.moderation_state === "REMOVED" ? (
                          <span className="ml-2 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600" data-testid={`admin-user-removed-${u.id}`}>
                            REMOVED
                          </span>
                        ) : u.is_active === false ? (
                          <span className="ml-2 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700" data-testid={`admin-user-blocked-${u.id}`}>
                            BLOCKED
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 pr-4 text-slate-600">{u.email || u.phone}</td>
                      <td className="py-3 pr-4 text-xs text-slate-600">{u.role === "MASTER_CLIENT" ? "CLIENT" : u.role.replaceAll("_", " ")}</td>
                      <td className="py-3 pr-4"><StatusBadge status={u.kyc_status} /></td>
                      <td className="py-3">
                        <div className="flex justify-end gap-2">
                          {u.moderation_state === "REMOVED" ? (
                            user?.role === "SUPER_ADMIN" && (
                              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-800" onClick={() => restoreUser(u.id)} data-testid={`admin-restore-${u.id}`}>
                                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restore
                              </Button>
                            )
                          ) : u.is_active === false ? (
                            <Button size="sm" variant="outline" onClick={() => unblockUser(u.id)} data-testid={`admin-unblock-${u.id}`}>
                              Unblock
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="border-orange-300 text-orange-700" onClick={() => setBlockTarget(u)} data-testid={`admin-block-${u.id}`}>
                              <Ban className="mr-1.5 h-3.5 w-3.5" /> Block
                            </Button>
                          )}
                          {user?.role === "SUPER_ADMIN" && u.id !== user?.id && u.moderation_state !== "REMOVED" && (
                            <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => setDeleteTarget(u)} data-testid={`admin-delete-${u.id}`}>
                              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {tab === "moderation" && (
          <Panel
            title="Blocked & removed accounts"
            description="Everyone currently blocked or removed, with the reason, who actioned it and when."
            testId="admin-moderation-panel"
          >
            {moderation.length ? (
              <ul className="divide-y divide-slate-100" data-testid="moderation-list">
                {moderation.map((u) => (
                  <li key={u.id} className="flex flex-wrap items-start justify-between gap-4 py-4" data-testid={`moderation-row-${u.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">
                        {u.full_name}
                        <span
                          className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                            u.moderation_state === "REMOVED"
                              ? "border-slate-300 bg-slate-100 text-slate-600"
                              : "border-red-200 bg-red-50 text-red-700"
                          }`}
                          data-testid={`moderation-state-${u.id}`}
                        >
                          {u.moderation_state}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500">
                        {u.email || u.phone} · {u.role === "MASTER_CLIENT" ? "CLIENT" : u.role.replaceAll("_", " ")}
                      </p>
                      <p className="mt-1 text-sm text-slate-700" data-testid={`moderation-reason-${u.id}`}>
                        {u.moderation_state === "REMOVED"
                          ? `Removed by ${u.deleted_by || "an administrator"}${u.deleted_at ? ` on ${u.deleted_at.slice(0, 16).replace("T", " ")}` : ""}`
                          : `${u.blocked_reason || "No reason recorded"} — by ${u.blocked_by || "an administrator"}${u.blocked_at ? ` on ${u.blocked_at.slice(0, 16).replace("T", " ")}` : ""}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {u.moderation_state === "REMOVED" ? (
                        user?.role === "SUPER_ADMIN" && (
                          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-800" onClick={() => restoreUser(u.id)} data-testid={`moderation-restore-${u.id}`}>
                            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restore
                          </Button>
                        )
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => unblockUser(u.id)} data-testid={`moderation-unblock-${u.id}`}>
                          Unblock
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500" data-testid="moderation-empty">No blocked or removed accounts.</p>
            )}
          </Panel>
        )}

        {tab === "team" && (
          <Panel title="Admin team" description="Only a super admin can create, invite or reassign administrators." testId="admin-team-panel">
            {user?.role === "SUPER_ADMIN" ? (
              <>
                <div className="grid gap-3 sm:grid-cols-4">
                  <input
                    placeholder="Full name"
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={inviteForm.full_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                    data-testid="invite-name-input"
                  />
                  <input
                    placeholder="Email address"
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    data-testid="invite-email-input"
                  />
                  <select
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    data-testid="invite-role-select"
                  >
                    <option value="ADMIN">Admin</option>
                    <option value="SUPER_ADMIN">Super Admin</option>
                  </select>
                  <Button
                    className="rounded-full bg-emerald-900"
                    disabled={!inviteForm.email || inviteForm.full_name.length < 2}
                    onClick={sendInvite}
                    data-testid="invite-send-btn"
                  >
                    <UserPlus className="mr-2 h-4 w-4" /> Send invite
                  </Button>
                </div>

                {inviteLink && (
                  <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="invite-link-panel">
                    <code className="min-w-0 flex-1 break-all text-xs text-emerald-900" data-testid="invite-link-value">{inviteLink}</code>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success("Link copied"); }} data-testid="invite-copy-btn">
                      Copy link
                    </Button>
                  </div>
                )}

                <h3 className="mt-8 text-sm font-bold uppercase tracking-wider text-slate-500">Create an administrator directly</h3>
                <p className="mb-3 text-xs text-slate-500">
                  They sign in at <code className="font-mono">/admin/login</code> and must change this temporary password immediately.
                </p>
                <div className="grid gap-3 sm:grid-cols-5">
                  <input
                    placeholder="Full name"
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={adminForm.full_name}
                    onChange={(e) => setAdminForm({ ...adminForm, full_name: e.target.value })}
                    data-testid="create-admin-name-input"
                  />
                  <input
                    placeholder="Email address"
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                    data-testid="create-admin-email-input"
                  />
                  <input
                    placeholder="Phone (+234…)"
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={adminForm.phone}
                    onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })}
                    data-testid="create-admin-phone-input"
                  />
                  <PasswordInput
                    placeholder="Temporary password"
                    className="h-10"
                    value={adminForm.temporary_password}
                    onChange={(e) => setAdminForm({ ...adminForm, temporary_password: e.target.value })}
                    testId="create-admin-password-input"
                  />
                  <select
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={adminForm.role}
                    onChange={(e) => setAdminForm({ ...adminForm, role: e.target.value })}
                    data-testid="create-admin-role-select"
                  >
                    <option value="ADMIN">Administrator</option>
                    <option value="SUPER_ADMIN">Super Administrator</option>
                  </select>
                </div>
                <Button
                  className="mt-3 rounded-full bg-emerald-900"
                  disabled={
                    adminForm.full_name.length < 2 ||
                    !adminForm.email ||
                    adminForm.phone.length < 7 ||
                    adminForm.temporary_password.length < 8
                  }
                  onClick={createAdmin}
                  data-testid="create-admin-btn"
                >
                  <UserPlus className="mr-2 h-4 w-4" /> Create administrator
                </Button>

                <h3 className="mt-8 text-sm font-bold uppercase tracking-wider text-slate-500">Current administrators</h3>
                <ul className="divide-y divide-slate-100" data-testid="admin-team-list">
                  {admins.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`admin-team-row-${a.id}`}>
                      <div className="flex min-w-0 items-center gap-2.5">
                        <Avatar name={a.full_name} src={a.avatar_url} size="sm" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900">{a.full_name}</p>
                          <p className="text-xs text-slate-500">
                            {a.email}
                            {a.must_change_password ? " · temporary password not yet changed" : ""}
                          </p>
                        </div>
                      </div>
                      {a.id === user?.id ? (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500" data-testid={`admin-team-self-${a.id}`}>
                          {a.role === "SUPER_ADMIN" ? "Super Administrator" : "Administrator"} · you
                        </span>
                      ) : (
                        <select
                          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                          value={a.role}
                          onChange={(e) => changeRole(a.id, e.target.value)}
                          data-testid={`admin-role-select-${a.id}`}
                        >
                          <option value="ADMIN">Administrator</option>
                          <option value="SUPER_ADMIN">Super Administrator</option>
                        </select>
                      )}
                    </li>
                  ))}
                </ul>

                <h3 className="mt-8 text-sm font-bold uppercase tracking-wider text-slate-500">Invitations</h3>
                <ul className="divide-y divide-slate-100" data-testid="admin-invites-list">
                  {invites.map((i) => (
                    <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`admin-invite-row-${i.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">{i.full_name} · {i.email}</p>
                        <p className="text-xs text-slate-500">
                          {i.role.replaceAll("_", " ")} · invited by {i.invited_by_email} · expires {new Date(i.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                          i.status === "PENDING" ? "border-amber-200 bg-amber-50 text-amber-700"
                          : i.status === "ACCEPTED" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-slate-50 text-slate-500"
                        }`} data-testid={`admin-invite-status-${i.id}`}>
                          {i.status}
                        </span>
                        {i.status === "PENDING" && (
                          <Button size="sm" variant="outline" onClick={() => revokeInvite(i.id)} data-testid={`admin-invite-revoke-${i.id}`}>
                            Revoke
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                  {invites.length === 0 && <li className="py-4 text-sm text-slate-500" data-testid="admin-invites-empty">No invitations yet.</li>}
                </ul>
              </>
            ) : (
              <p className="text-sm text-slate-500" data-testid="admin-team-restricted">
                Only a super admin can invite or revoke admin accounts.
              </p>
            )}
          </Panel>
        )}

        {tab === "security" && (
          <Panel title="Change your password" description="Replace the seeded password with one only you know." testId="admin-security-panel">
            <div className="grid max-w-md gap-3">
              <PasswordInput
                placeholder="Current password"
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                testId="pw-current-input"
              />
              <PasswordInput
                placeholder="New password (min 8 characters)"
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                testId="pw-new-input"
              />
              <Button
                className="rounded-full bg-emerald-900"
                disabled={!pw.current_password || pw.new_password.length < 8}
                onClick={changePassword}
                data-testid="pw-submit-btn"
              >
                Update password
              </Button>
              <p className="text-xs text-slate-500">
                Signed in as {user?.email} ({user?.role?.replaceAll("_", " ").toLowerCase()}). Any outstanding reset links are cancelled.
              </p>
            </div>
          </Panel>
        )}

        {tab === "audit" && (
          <Panel title="Audit log" description="Every administrative and user action is recorded." testId="admin-audit-panel">
            <ul className="divide-y divide-slate-100" data-testid="admin-audit-list">
              {logs.map((l) => (
                <li key={l.id} className="py-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-slate-900">{l.action.replaceAll("_", " ")}</span>
                    <span className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {l.actor_email || "system"} · {l.entity_type}{l.entity_id ? ` #${l.entity_id.slice(-6)}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>KYC review</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-5" data-testid="admin-kyc-detail">
              <div className="flex items-center gap-3">
                <StatusBadge status={detail.status} testId="admin-detail-status" />
                <span className="text-xs text-slate-500">{detail.role.replaceAll("_", " ")}</span>
              </div>
              {detail.identity_check && (
                <div className="rounded-xl border border-slate-200 bg-stone-50 p-4" data-testid="admin-identity-check">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Identity verification (Dojah)</p>
                  <p className="mt-1.5 text-sm font-semibold text-slate-900" data-testid="admin-identity-status">
                    {detail.identity_check.id_type || "ID"} {detail.identity_check.masked || ""} ·{" "}
                    {String(detail.identity_check.status).replaceAll("_", " ")}
                    {detail.identity_check.sandbox ? " (sandbox)" : ""}
                  </p>
                  {detail.identity_check.matched_name && (
                    <p className="mt-1 text-xs text-slate-600">
                      Government record: {detail.identity_check.matched_name}
                      {detail.identity_check.date_of_birth ? ` · ${detail.identity_check.date_of_birth}` : ""} ·{" "}
                      <span className={detail.identity_check.name_match ? "font-semibold text-emerald-700" : "font-semibold text-red-700"}>
                        {detail.identity_check.name_match ? "name matches" : "name does NOT match"}
                      </span>
                    </p>
                  )}
                  {detail.identity_check.message && (
                    <p className="mt-1 text-xs text-slate-500">{detail.identity_check.message}</p>
                  )}
                </div>
              )}
              <dl className="grid gap-3 sm:grid-cols-2">
                {Object.entries(detail.payload || {})
                  .filter(([k]) => !k.endsWith("_file_id"))
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k.replaceAll("_", " ")}</dt>
                      <dd className="mt-0.5 break-words text-sm text-slate-800">
                        {typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}
                      </dd>
                    </div>
                  ))}
              </dl>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Private documents (signed links)</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(detail.document_urls || {}).map(([label, url]) => (
                    <a
                      key={label}
                      href={`${BACKEND_URL}${url}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-doc-${label}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-400"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> {label.replaceAll("_file_id", "").replaceAll("_", " ")}
                    </a>
                  ))}
                  {Object.keys(detail.document_urls || {}).length === 0 && (
                    <span className="text-sm text-slate-500">No documents attached (seeded record).</span>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Verification history</p>
                <ul className="mt-2 space-y-1.5 text-xs text-slate-600" data-testid="admin-detail-history">
                  {(detail.history || []).map((h, i) => (
                    <li key={i}>
                      <strong>{h.action.replaceAll("_", " ")}</strong> · {h.by} · {new Date(h.at).toLocaleString()}
                      {h.notes ? ` — ${h.notes}` : ""}
                    </li>
                  ))}
                </ul>
              </div>

              <Textarea placeholder="Reviewer notes (required to reject or request correction)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-notes-input" />

              <div className="flex flex-wrap gap-2">
                <Button className="rounded-full bg-emerald-900 hover:bg-emerald-800" onClick={() => act("approve")} data-testid="admin-approve-btn">Approve</Button>
                <Button variant="outline" className="rounded-full" onClick={() => act("review")} data-testid="admin-review-btn">Mark under review</Button>
                <Button variant="outline" className="rounded-full border-orange-300 text-orange-700" onClick={() => act("request-correction")} data-testid="admin-correction-btn">Request correction</Button>
                <Button variant="outline" className="rounded-full border-red-300 text-red-700" onClick={() => act("reject")} data-testid="admin-reject-btn">Reject</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!blockTarget} onOpenChange={(o) => !o && setBlockTarget(null)}>
        <DialogContent data-testid="block-user-dialog">
          <DialogHeader><DialogTitle>Block {blockTarget?.full_name}?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              They will be signed out and refused login until an administrator unblocks them. Their records are kept.
            </p>
            <Textarea
              placeholder="Reason for blocking (recorded in the audit log and shown to the user)"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              data-testid="block-reason-input"
            />
            <Button className="w-full rounded-full bg-orange-700 hover:bg-orange-800" disabled={blockReason.trim().length < 5} onClick={blockUser} data-testid="block-confirm-btn">
              Block this account
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>Remove {deleteTarget?.full_name}?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              The account is hidden and can no longer sign in, but its records are kept — a super admin can restore it
              from the <strong>Moderation</strong> tab. Accounts with money still held in escrow cannot be removed.
            </p>
            <Button className="w-full rounded-full bg-red-700 hover:bg-red-800" onClick={deleteUser} data-testid="delete-confirm-btn">
              Remove this account
            </Button>
            <Button variant="outline" className="w-full rounded-full" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel-btn">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
